import React, { useState, useEffect } from 'react';
import { Button, Select, TextInput, Label } from '../../../components/ui';
import { WorkflowRule } from '../useData';
import { getFieldsForEntity, EntityField } from '../utils/entityFields';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import useSWR from 'swr';
import api from '../../../services/api';

interface Condition {
  field: string;
  operator: string;
  value: any;
}

interface ConditionBuilderProps {
  conditions: Condition[];
  onChange: (conditions: Condition[]) => void;
  entityType?: string;
}

const operators = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Not Contains' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'greater_than_or_equal', label: 'Greater Than or Equal' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'less_than_or_equal', label: 'Less Than or Equal' },
  { value: 'is_empty', label: 'Is Empty' },
  { value: 'is_not_empty', label: 'Is Not Empty' },
  { value: 'in', label: 'In' },
  { value: 'not_in', label: 'Not In' },
];

const ConditionBuilder: React.FC<ConditionBuilderProps> = ({
  conditions = [],
  onChange,
  entityType = 'Task',
}) => {
  const { colors } = useTheme();
  const [logic, setLogic] = useState<'AND' | 'OR'>('AND');
  const availableFields = getFieldsForEntity(entityType);

  const addCondition = () => {
    onChange([
      ...conditions,
      {
        field: '',
        operator: 'equals',
        value: '',
      },
    ]);
  };

  const updateCondition = (index: number, updates: Partial<Condition>) => {
    const updated = [...conditions];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const removeCondition = (index: number) => {
    const updated = conditions.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div className="space-y-4">
      {conditions.length > 1 && (
        <div className="flex items-center gap-2">
          <Label className={cn('text-sm font-medium mb-0', colors.textSecondary)}>Logic</Label>
          <Select
            value={logic}
            onChange={(e) => setLogic(e.target.value as 'AND' | 'OR')}
            options={[
              { value: 'AND', label: 'AND (All conditions must be true)' },
              { value: 'OR', label: 'OR (Any condition can be true)' },
            ]}
          />
        </div>
      )}

      <div className="space-y-3">
        {conditions.map((condition, index) => {
          const selectedField = availableFields.find(f => f.name === condition.field);
          return (
            <ConditionRow
              key={index}
              condition={condition}
              selectedField={selectedField}
              index={index}
              updateCondition={updateCondition}
              removeCondition={removeCondition}
              entityType={entityType}
              availableFields={availableFields}
            />
          );
        })}
      </div>

      <Button variant="outline" onClick={addCondition}>
        + Add Condition
      </Button>
    </div>
  );
};

// Component to handle fetching and displaying foreign key values
interface ConditionRowProps {
  condition: Condition;
  selectedField: EntityField | undefined;
  index: number;
  updateCondition: (index: number, updates: Partial<Condition>) => void;
  removeCondition: (index: number) => void;
  entityType: string;
  availableFields: EntityField[];
}

const ConditionRow: React.FC<ConditionRowProps> = ({
  condition,
  selectedField,
  index,
  updateCondition,
  removeCondition,
  entityType,
  availableFields,
}) => {
  const { colors } = useTheme();
  // Fetch related entities if this is a foreign key field
  const { data: relatedEntities, isLoading: isLoadingRelated } = useSWR<any[]>(
    selectedField?.apiEndpoint && condition.field ? selectedField.apiEndpoint : null,
    async (url: string) => {
      const response = await api.get(url);
      return Array.isArray(response.data) ? response.data : [];
    }
  );

  // Build options from related entities
  const relatedOptions = React.useMemo(() => {
    if (!selectedField?.apiEndpoint || !relatedEntities) return [];
    
    const valueField = selectedField.valueField || 'id';
    const labelField = selectedField.labelField || 'name';
    
    return relatedEntities.map((entity: any) => ({
      value: entity[valueField],
      label: entity[labelField] || String(entity[valueField]),
    }));
  }, [relatedEntities, selectedField]);

  return (
    <div className={cn('flex items-start gap-2 p-3 border rounded-lg', colors.surfaceHover, colors.border)}>
      <div className="flex-1 grid grid-cols-3 gap-2">
        <Select
          value={condition.field}
          onChange={(e) => {
            const fields = getFieldsForEntity(entityType);
            const field = fields.find(f => f.name === e.target.value);
            updateCondition(index, { 
              field: e.target.value,
              value: field?.type === 'boolean' ? false : field?.type === 'number' ? 0 : ''
            });
          }}
          options={availableFields.map((field) => ({
            value: field.name,
            label: field.label,
          }))}
        />
        <Select
          value={condition.operator}
          onChange={(e) => updateCondition(index, { operator: e.target.value })}
          options={operators.map((op) => ({ value: op.value, label: op.label }))}
        />
        {!['is_empty', 'is_not_empty'].includes(condition.operator) && condition.field && (
          selectedField?.type === 'select' && selectedField.options ? (
            <Select
              value={String(condition.value || '')}
              onChange={(e) => updateCondition(index, { value: e.target.value })}
              options={[
                { value: '', label: 'Select Value' },
                ...selectedField.options.map((opt) => ({ value: String(opt.value), label: opt.label })),
              ]}
            />
          ) : selectedField?.apiEndpoint && selectedField.type === 'number' ? (
            <Select
              value={String(condition.value || '')}
              onChange={(e) => updateCondition(index, { value: Number(e.target.value) || 0 })}
              disabled={isLoadingRelated}
              options={[
                { value: '', label: isLoadingRelated ? 'Loading...' : 'Select Value' },
                ...relatedOptions.map((opt) => ({ value: String(opt.value), label: opt.label })),
              ]}
            />
          ) : selectedField?.type === 'boolean' ? (
            <Select
              value={String(condition.value || 'false')}
              onChange={(e) => updateCondition(index, { value: e.target.value === 'true' })}
              options={[
                { value: 'true', label: 'True' },
                { value: 'false', label: 'False' },
              ]}
            />
          ) : selectedField?.type === 'date' ? (
            <TextInput
              type="date"
              value={condition.value ? new Date(condition.value).toISOString().split('T')[0] : ''}
              onChange={(e) => updateCondition(index, { value: e.target.value })}
            />
          ) : selectedField?.type === 'number' ? (
            <TextInput
              type="number"
              placeholder="Enter number"
              value={String(condition.value || '')}
              onChange={(e) => updateCondition(index, { value: Number(e.target.value) || 0 })}
            />
          ) : (
            <TextInput
              placeholder="Enter value"
              value={String(condition.value || '')}
              onChange={(e) => updateCondition(index, { value: e.target.value })}
            />
          )
        )}
      </div>
      <Button
        variant="danger"
        size="sm"
        onClick={() => removeCondition(index)}
      >
        Remove
      </Button>
    </div>
  );
};

export default ConditionBuilder;
